import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { getBaseUrl, getToken } from "../../lib/storage";

interface ModelOption {
  provider: string;
  model: string;
  is_default: boolean;
}

async function fetchModels(): Promise<ModelOption[]> {
  try {
    const r = await fetch(`${getBaseUrl()}/api/v1/models`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!r.ok) return [];
    const data = await r.json();
    return data.models || [];
  } catch {
    return [];
  }
}

interface Props {
  value: string;
  onChange: (provider: string, model: string) => void;
}

export function ModelSelector({ value, onChange }: Props) {
  const [models, setModels] = useState<ModelOption[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchModels().then(setModels);
  }, []);

  if (models.length === 0) return null;

  const current = models.find((m) => `${m.provider}/${m.model}` === value) || models[0];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2 py-1 rounded-card border border-border/60 bg-bg-subtle text-small text-fg-muted hover:text-fg transition-colors"
      >
        <span className="capitalize">{current?.provider}</span>
        <span className="text-fg-subtle">·</span>
        <span className="max-w-[120px] truncate">{current?.model}</span>
        <ChevronDown className="size-3 shrink-0" />
      </button>

      {open && (
        <div className="absolute bottom-full mb-1 left-0 z-50 w-64 rounded-card border border-border/60 bg-bg shadow-card overflow-hidden">
          {/* Group by provider */}
          {Array.from(new Set(models.map((m) => m.provider))).map((provider) => (
            <div key={provider}>
              <div className="px-3 py-1.5 text-small font-medium text-fg-muted capitalize border-b border-border/40">
                {provider}
              </div>
              {models
                .filter((m) => m.provider === provider)
                .map((m) => {
                  const key = `${m.provider}/${m.model}`;
                  const active = key === value;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => { onChange(m.provider, m.model); setOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-small hover:bg-bg-subtle transition-colors ${active ? "text-accent-400" : "text-fg"}`}
                    >
                      {m.model}
                      {m.is_default && <span className="ml-2 text-fg-muted text-xs">default</span>}
                    </button>
                  );
                })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
